import { useRef, useState } from "react";
import { findWhere } from "underscore";
import { useAtomValue } from "jotai";

import useUserActivity from "../../api/api-hooks/userActivity/useUserActivity";
import DropdownOptionWithIcon from "../Menus/DropdownOptionWithIcon";
import ManageItemsDrawer from "../Drawers/ManageItemsDrawer";
import useReadableText from "../../hooks/useReadableText";
import CreateFilterDrawer from "./CreateFilterDrawer";
import { filterIsSame } from "../../utils/QueryUtils";
import NewButton from "../../elements/NewButton";
import FiltersButton from "./FiltersButton";
import StrUtils from "../../utils/StrUtils";
import { IsMobileAtom } from "../../App";

const FiltersButtonWithOptions = ({
  currentModule,
  stateQuery, // RecordDetailViewSubModule.Query or PageView.Query
  filters, // Saved Filters
  query,
  setQuery,
  onApply,
  onClear,
  onSave,
  submitting,
  clearFilter,
  createFilter,
  updateFilter,
  deleteFilter,
  reorderFilters,
  title,
  updateKey,
  createKey,
  emptyDescription,
  subitemFieldsOnly = false,
  checkForFields = false,
  subitemQuery = false,
  showVariables = false,
  view,
  page,
  disableSave,
  disableAdvancedFilter,
}) => {
  const mobileScreen = useAtomValue(IsMobileAtom);
  const { setUserActivity } = useUserActivity();
  const [editFilter, setEditFilter] = useState(null);
  const [prefillFilter, setPrefillFilter] = useState(null);
  const [createDrawerOpen, setCreateDrawerOpen] = useState(false);
  const [manageDrawerOpen, setManageDrawerOpen] = useState(false);
  const [dontReopen, setDontReopen] = useState(false);
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const clearRef = useRef();
  const saveAsRef = useRef();
  const hijackRef = useRef(); // Hijacks the whisper ref used in FiltersButton component. This allows us to reopen the FiltersOptions component.

  const getFilterName = (id) => {
    const filter = findWhere(filters, { id });

    return filter?.Name ?? "Filters Applied";
  };

  const { getReadableText } = useReadableText({
    moduleAPIName: currentModule?.APIName,
    fallbackMessage: "None",
  });

  const invalidFilter =
    query?.Metadata?.PageFilter || !query?.Filters || !query?.Filters?.length;
  const invalidDefaultFilter = filterIsSame(query, stateQuery);

  const handleSetQuery = (newQuery) => {
    const q = structuredClone(newQuery);
    if (q?.Metadata?.PageFilter) {
      delete q.Metadata.PageFilter;
    }

    if (subitemQuery) {
      setQuery([
        {
          QueryField: subitemFieldsOnly,
          Query: q,
        },
      ]);
    } else {
      setQuery(q);
    }
  };

  // Set User Activity "queried-page-view-table" when the queried records change
  const handleUserActivity = (newQuery) => {
    if (!view || !view.id) return;
    const queriedPageViewTableActivity = {
      id: StrUtils.uuidv4(),
      Type: "queried-page-view-table",
      Page: {
        id: page?.id,
        Name: page?.Name,
        Logo: page?.Icon,
      },
      View: {
        id: view?.id,
        Name: view?.Name,
        Type: view?.Type,
      },
      Readable: getReadableText(newQuery),
    };
    setUserActivity({
      Activity: queriedPageViewTableActivity,
    });
  };

  const handleUpdateFilter = (filter) => {
    updateFilter(filter);
    handleUpdateFilters(filter.payload);
  };

  const handleUpdateFilters = (filter) => {
    if (filter) {
      setQuery((prev) => {
        const finalQuery = {
          ...prev,
          Filters: filter.Filters,
          Metadata: filter.Metadata,
          Limit: filter.Limit,
        };

        return finalQuery;
      });
    }

    return subitemQuery
      ? (filter) => {
          const newQuery = { ...query };
          let newMetadata;

          if (newQuery.Metadata) {
            newMetadata = {
              ...newQuery.Metadata,
              PageFilter: filter.id,
              Type: filter.Type,
            };
          } else {
            newMetadata = {
              PageFilter: filter.id,
              Type: filter.Type,
            };
          }

          const finalQuery = {
            ...newQuery,
            Filters: filter.Filters,
            Metadata: newMetadata,
          };

          if (filter?.Limit?.Count) {
            finalQuery.Limit = { Count: filter.Limit.Count };
          } else {
            finalQuery.Limit = {};
          }

          setQuery([
            {
              QueryField: subitemFieldsOnly,
              Query: finalQuery,
            },
          ]);

          handleUserActivity(finalQuery);
        }
      : (filter) => {
          setQuery((prev) => {
            let newMetadata;

            if (prev.Metadata) {
              newMetadata = {
                ...prev.Metadata,
                PageFilter: filter.id,
                Type: filter.Type,
              };
            } else {
              newMetadata = {
                PageFilter: filter.id,
                Type: filter.Type,
              };
            }

            const finalQuery = {
              ...prev,
              Filters: filter.Filters,
              Metadata: newMetadata,
            };

            if (filter?.Limit?.Count) {
              finalQuery.Limit = { Count: filter.Limit.Count };
            } else {
              finalQuery.Limit = {};
            }

            handleUserActivity(finalQuery);

            return finalQuery;
          });
        };
  };

  return (
    <>
      <FiltersButton
        currentModule={currentModule}
        savedQuery={stateQuery}
        query={query}
        filters={filters}
        setQuery={(newQuery) => handleSetQuery(newQuery)}
        onApply={onApply}
        onClear={onClear}
        onSave={onSave}
        clearFilter={clearFilter}
        keepOpen={saveAsOpen || clearOpen}
        subitemFieldsOnly={subitemFieldsOnly}
        checkForFields={checkForFields}
        showVariables={showVariables}
        getFilterName={getFilterName}
        customClearButton={(ref) => (
          <NewButton
            title={mobileScreen ? "Clear" : "Clear Filters"}
            color="secondary"
            newButtonRef={clearRef}
            onOpen={() => setClearOpen(true)}
            onClose={() => setClearOpen(false)}
            disabled={invalidDefaultFilter && !query?.Filters?.length}
            options={
              <>
                <DropdownOptionWithIcon
                  name="Switch to Default Filter"
                  onClick={() => {
                    ref?.current?.close();
                    clearRef?.current?.close();

                    if (subitemQuery) {
                      setQuery([
                        {
                          QueryField: subitemFieldsOnly,
                          Query: stateQuery,
                        },
                      ]);
                    } else {
                      setQuery(stateQuery);
                    }
                  }}
                  icon={"mdi mdi-cached"}
                  disabled={invalidDefaultFilter}
                />
                <DropdownOptionWithIcon
                  name="Clear all Filters"
                  onClick={() => {
                    ref?.current?.close();
                    clearRef?.current?.close();
                    clearFilter();
                  }}
                  icon={"mdi mdi-close-box"}
                  disabled={!query?.Filters?.length}
                />
              </>
            }
          />
        )}
        customSaveButton={(ref) => (
          <NewButton
            title={mobileScreen ? "Save" : "Save As"}
            newButtonRef={saveAsRef}
            onOpen={() => setSaveAsOpen(true)}
            onClose={() => setSaveAsOpen(false)}
            disabled={invalidFilter && invalidDefaultFilter}
            options={
              <>
                <DropdownOptionWithIcon
                  name="Save As New Filter"
                  onClick={() => {
                    ref?.current?.close();
                    saveAsRef?.current?.close();
                    setPrefillFilter(query);
                    setCreateDrawerOpen(true);
                  }}
                  icon={"mdi mdi-content-save-move"}
                  disabled={invalidFilter}
                />
                <DropdownOptionWithIcon
                  name="Save As Default Filter"
                  onClick={() => {
                    saveAsRef?.current?.close();
                    onSave(query?.Filters, query?.Metadata?.Type);
                  }}
                  icon={"mdi mdi-content-save"}
                  disabled={invalidDefaultFilter}
                />
              </>
            }
          />
        )}
        saveNewFilter={() => {
          setPrefillFilter(query);
          setCreateDrawerOpen(true);
        }}
        setManageDrawerOpen={setManageDrawerOpen}
        handleUpdateFilters={handleUpdateFilters}
        disableSave={disableSave}
        disableAdvancedFilter={disableAdvancedFilter}
      />
      <CreateFilterDrawer
        key={editFilter?.id ?? "Create"}
        open={createDrawerOpen}
        onClose={() => setCreateDrawerOpen(false)}
        onExited={() => {
          setEditFilter(null);

          if (dontReopen) {
            setDontReopen(false);
          } else {
            hijackRef?.current?.open();
          }
        }}
        currentModule={currentModule}
        submitting={submitting}
        handleCreate={createFilter}
        handleUpdate={handleUpdateFilter}
        existingFilter={editFilter}
        prefillFilter={prefillFilter}
        createKey={createKey}
        updateKey={updateKey}
        subitemFieldsOnly={subitemFieldsOnly}
        checkForFields={checkForFields}
        showVariables={showVariables}
      />
      <ManageItemsDrawer
        open={manageDrawerOpen}
        onClose={() => {
          setManageDrawerOpen(false);
          hijackRef?.current?.open();
        }}
        items={filters}
        onEdit={(val) => {
          setEditFilter(val);
          setCreateDrawerOpen(true);
          setDontReopen(true);
        }}
        onDelete={deleteFilter}
        onReorder={reorderFilters}
        title={title}
        itemKey={updateKey}
        description={emptyDescription}
      />
    </>
  );
};

export default FiltersButtonWithOptions;
